/**
 * Before/After comparison slider.
 *
 * THE PROBLEM THIS SOLVES: the handle was a static circle. Nothing told a
 * visitor it moved, so the "after" - the finished painting, which is the whole
 * point of the section - went unseen unless someone guessed to drag it.
 *
 * Three affordances, in order of how much they carry:
 *   1. An intro nudge. When the section first scrolls into view the handle
 *      sweeps right, left, then settles at centre. The visitor watches the
 *      painting appear rather than being asked to work it out.
 *   2. A pulse ring on the handle, which runs until the first interaction.
 *   3. Cursor and hover states that say "grab me".
 *
 * All three stop for good the moment the visitor touches the slider - a hint
 * that keeps hinting after it has been understood is just noise.
 *
 * Also adds keyboard support. The handle was aria-hidden with no tabindex, so
 * a keyboard or screen-reader user had no way to reveal the after image.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initSlider(slider) {
    if (slider.dataset.baInit === '1') return;
    slider.dataset.baInit = '1';

    var before = slider.querySelector('.ba-before');
    var handle = slider.querySelector('.ba-handle');
    if (!before || !handle) return;

    var pct = 50;
    var dragging = false;
    var touched = false;   // has the visitor interacted yet
    var hintTimer = null;

    function updateWidthVar() {
      slider.style.setProperty('--ba-slider-width', slider.offsetWidth + 'px');
    }
    updateWidthVar();
    window.addEventListener('resize', updateWidthVar);

    function apply(p) {
      pct = Math.max(0, Math.min(100, p));
      before.style.width = pct + '%';
      handle.style.left = pct + '%';
      handle.setAttribute('aria-valuenow', Math.round(pct));
    }

    function fromClientX(x) {
      var rect = slider.getBoundingClientRect();
      apply(((x - rect.left) / rect.width) * 100);
    }

    /* Once the visitor takes over, every hint stops permanently. */
    function markTouched() {
      if (touched) return;
      touched = true;
      clearTimeout(hintTimer);
      slider.classList.remove('is-hinting');
      slider.classList.add('has-interacted');
    }

    /* ---------------------------------------------------------- pointer */
    function down(e) {
      markTouched();
      dragging = true;
      slider.classList.add('is-dragging');
      if (handle.setPointerCapture && e.pointerId !== undefined) {
        try { handle.setPointerCapture(e.pointerId); } catch (err) {}
      }
      fromClientX(e.clientX);
    }

    function move(e) {
      if (!dragging) return;
      if (e.cancelable) e.preventDefault();
      fromClientX(e.clientX);
    }

    function up() {
      dragging = false;
      slider.classList.remove('is-dragging');
    }

    if (window.PointerEvent) {
      slider.addEventListener('pointerdown', down);
      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    } else {
      slider.addEventListener('mousedown', function (e) { e.preventDefault(); down(e); });
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      slider.addEventListener('touchstart', function (e) { down(e.touches[0]); }, { passive: true });
      slider.addEventListener('touchmove', function (e) {
        if (!dragging) return;
        if (e.cancelable) e.preventDefault();
        fromClientX(e.touches[0].clientX);
      }, { passive: false });
      slider.addEventListener('touchend', up);
    }

    /* Hovering the slider is intent enough to stop the pulse. */
    slider.addEventListener('mouseenter', markTouched);

    /* --------------------------------------------------------- keyboard */
    handle.addEventListener('keydown', function (e) {
      var step = e.shiftKey ? 10 : 2;
      var handled = true;
      switch (e.key) {
        case 'ArrowLeft':  markTouched(); apply(pct - step); break;
        case 'ArrowRight': markTouched(); apply(pct + step); break;
        case 'Home':       markTouched(); apply(0); break;
        case 'End':        markTouched(); apply(100); break;
        default: handled = false;
      }
      if (handled) e.preventDefault();
    });
    handle.addEventListener('focus', markTouched);

    /* ------------------------------------------------------- intro nudge */
    function runHint() {
      if (touched || REDUCED) return;
      slider.classList.add('is-hinting');
      var stops = [72, 30, 50];
      var i = 0;
      (function next() {
        if (touched) { slider.classList.remove('is-hinting'); return; }
        if (i >= stops.length) {
          slider.classList.remove('is-hinting');
          return;
        }
        apply(stops[i++]);
        hintTimer = setTimeout(next, 680);
      })();
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          // A short beat so the nudge reads as deliberate, not as a glitch
          // while the section is still settling into place.
          hintTimer = setTimeout(runHint, 420);
        });
      }, { threshold: 0.55 });
      io.observe(slider);
    }

    apply(50);
  }

  function boot() { document.querySelectorAll('.ba-slider').forEach(initSlider); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
